import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import ResponsiveTable, { ResponsiveTableColumn } from '../common/ResponsiveTable';
import apiService from '../../services/api.service';
import type { McsrBulkCatalog, McsrBulkOutputs, McsrBulkProject, McsrBulkRun } from '../../types/mcsr';
import {
  codeOptions,
  dropChildren,
  familyRows,
  projectMatches,
  selectionInOrder,
  toggleAllShown,
  toggleProject,
} from '../../utils/mcsrBulk';

/**
 * MCSR > Monthly Cost Status Report > Bulk print / export. Choose projects (by number or name, division, department;
 * a parent brings its children unless "Parent projects only"), then the outputs: one PDF with every project for
 * printing (each on its own pages with its own page numbers), a ZIP with one PDF per project, and an Excel workbook
 * (a summary sheet, then a sheet per project). Each project is printed as the single PDF / Excel export prints it.
 * The run works in the background; the list below shows its progress and the files (kept a week).
 */

interface Props {
  open: boolean;
  onClose: () => void;
  /** The report page's month ('YYYY-MM'), the dialog's first choice. */
  defaultMonth: string;
}

type Row = { p: McsrBulkProject; child: boolean };

function monthText(month: string): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const errorText = (e: any, fallback: string): string => e?.response?.data?.message || e?.message || fallback;

const STATUS_COLOR: Record<McsrBulkRun['status'], 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  Queued: 'default', Running: 'info', Done: 'success', Failed: 'error', Cancelled: 'warning',
};

const McsrBulkDialog: React.FC<Props> = ({ open, onClose, defaultMonth }) => {
  const [months, setMonths] = useState<string[]>([]);
  const [month, setMonth] = useState(defaultMonth);
  const [catalog, setCatalog] = useState<McsrBulkCatalog | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [division, setDivision] = useState('');
  const [department, setDepartment] = useState('');
  const [parentsOnly, setParentsOnly] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [outputs, setOutputs] = useState<McsrBulkOutputs>({ pdf: true, zip: true, xlsx: false });
  const [starting, setStarting] = useState(false);
  const [runs, setRuns] = useState<McsrBulkRun[]>([]);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    if (open) setMonth(defaultMonth);
  }, [open, defaultMonth]);

  // The months with an MCSR (any project of the caller), newest first.
  useEffect(() => {
    if (!open) return;
    apiService.getMcsrMonths()
      .then((res: any) => setMonths(Array.from(new Set<string>((res.data || []).map((d: string) => String(d).slice(0, 7))))))
      .catch(() => setMonths([]));
  }, [open]);

  useEffect(() => {
    if (!open || !month) return;
    let live = true;
    setLoading(true);
    setLoadError(null);
    apiService.getMcsrBulkProjects(month)
      .then((res: any) => {
        if (!live) return;
        const data: McsrBulkCatalog = res.data || { projects: [], sapProjects: false };
        setCatalog(data);
        // Keep what is still printable this month.
        setSelected((prev) => new Set(Array.from(prev).filter((code) => data.projects.some((p) => p.project === code && p.hasReport))));
      })
      .catch((e) => { if (live) setLoadError(errorText(e, 'The projects could not be loaded')); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [open, month]);

  const loadRuns = useCallback(async () => {
    try {
      const res: any = await apiService.getMcsrBulkRuns();
      setRuns(res.data || []);
    } catch {
      /* the list is a convenience; the next poll tries again */
    }
  }, []);

  useEffect(() => {
    if (open) void loadRuns();
  }, [open, loadRuns]);

  const busy = runs.some((r) => r.status === 'Queued' || r.status === 'Running');
  useEffect(() => {
    if (!open || !busy) return undefined;
    const timer = setInterval(() => { void loadRuns(); }, 2500);
    return () => clearInterval(timer);
  }, [open, busy, loadRuns]);

  const projects = useMemo(() => catalog?.projects ?? [], [catalog]);
  const filter = { query, division, department, parentsOnly };
  const shown = useMemo(
    () => projects.filter((p) => projectMatches(p, filter)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, query, division, department, parentsOnly],
  );
  const rows: Row[] = useMemo(() => familyRows(shown), [shown]);
  const divisions = useMemo(() => codeOptions(projects, 'division'), [projects]);
  const departments = useMemo(() => codeOptions(projects, 'department'), [projects]);
  const pickable = shown.filter((p) => p.hasReport);
  const allShownOn = pickable.length > 0 && pickable.every((p) => selected.has(p.project));
  const someShownOn = pickable.some((p) => selected.has(p.project));
  const childCount = projects.filter((p) => p.parent && selected.has(p.project)).length;
  const label = monthText(month);
  const zipUseful = selected.size > 1;
  const chosenOutputs = { ...outputs, zip: outputs.zip && zipUseful };
  const canStart = selected.size > 0 && (chosenOutputs.pdf || chosenOutputs.zip || chosenOutputs.xlsx) && !starting;

  const setParents = (on: boolean) => {
    setParentsOnly(on);
    if (on) setSelected((prev) => dropChildren(prev, projects));
  };

  const start = async () => {
    setStarting(true);
    setNotice(null);
    try {
      const res: any = await apiService.startMcsrBulkRun({
        month,
        projects: selectionInOrder(selected, projects),
        parentsOnly,
        outputs: chosenOutputs,
      });
      const skipped: Array<{ project: string; message: string }> = res.data?.skipped || [];
      setNotice({
        severity: 'success',
        text: `Started: ${res.data?.projects ?? selected.size} project${(res.data?.projects ?? selected.size) === 1 ? '' : 's'}`
          + `${skipped.length ? `; left out ${skipped.map((s) => `${s.project} (${s.message})`).join(', ')}` : ''}. The files appear below when ready.`,
      });
      await loadRuns();
    } catch (e) {
      setNotice({ severity: 'error', text: errorText(e, 'The run could not be started') });
    } finally {
      setStarting(false);
    }
  };

  const download = async (run: McsrBulkRun, kind: 'pdf' | 'zip' | 'xlsx') => {
    setDownloading(`${run.runId}-${kind}`);
    try {
      const { blob, fileName } = await apiService.downloadMcsrBulkRun(run.runId, kind);
      downloadBlob(blob, fileName);
    } catch (e) {
      setNotice({ severity: 'error', text: errorText(e, 'The file could not be downloaded') });
    } finally {
      setDownloading(null);
    }
  };

  const cancel = async (run: McsrBulkRun) => {
    try {
      await apiService.cancelMcsrBulkRun(run.runId);
      await loadRuns();
    } catch (e) {
      setNotice({ severity: 'error', text: errorText(e, 'The run could not be cancelled') });
    }
  };

  const columns: ResponsiveTableColumn<Row>[] = [
    {
      id: 'pick',
      header: 'Print',
      cell: ({ p }) => (
        <Checkbox
          size="small"
          checked={selected.has(p.project)}
          disabled={!p.hasReport}
          onChange={() => setSelected((prev) => toggleProject(prev, p, projects, parentsOnly))}
          inputProps={{ 'aria-label': `Select ${p.project}` }}
        />
      ),
      width: 48,
    },
    {
      id: 'project',
      header: 'Project',
      cell: ({ p, child }) => (
        <Box sx={{ pl: child ? 2.5 : 0 }}>
          <Typography variant="body2" fontWeight={child ? 400 : 600}>{child ? '↳ ' : ''}{p.project}</Typography>
          {p.parent ? <Typography variant="caption" color="text.secondary">child of {p.parent}</Typography> : null}
        </Box>
      ),
      width: 150,
    },
    { id: 'name', header: 'Name', cell: ({ p }) => <Typography variant="body2">{p.projectName}</Typography> },
    {
      id: 'customer',
      header: 'Customer',
      hideOnMobile: true,
      cell: ({ p }) => <Typography variant="body2">{[p.customerCode, p.customerName].filter(Boolean).join(' ')}</Typography>,
    },
    {
      id: 'dims',
      header: 'Division / Department',
      hideOnMobile: true,
      cell: ({ p }) => (
        <Typography variant="body2">
          {[p.division, p.department].filter(Boolean).join(' / ') || '—'}
        </Typography>
      ),
      width: 150,
    },
    {
      id: 'status',
      header: label,
      cell: ({ p }) => (p.hasReport
        ? <Chip size="small" label="MCSR ready" color="success" variant="outlined" />
        : <Chip size="small" label="No MCSR" variant="outlined" />),
      width: 110,
    },
  ];

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="mcsr-bulk-title">
      <DialogTitle id="mcsr-bulk-title">Bulk print and export</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="mcsr-bulk-month">Month</InputLabel>
              <Select labelId="mcsr-bulk-month" label="Month" value={month} onChange={(e) => setMonth(String(e.target.value))}>
                {(months.includes(month) ? months : [month, ...months]).map((m) => (
                  <MenuItem key={m} value={m}>{monthText(m)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Project number or name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 170 }} disabled={!divisions.length}>
              <InputLabel id="mcsr-bulk-division">Division</InputLabel>
              <Select labelId="mcsr-bulk-division" label="Division" value={division} onChange={(e) => setDivision(String(e.target.value))}>
                <MenuItem value="">All divisions</MenuItem>
                {divisions.map((d) => <MenuItem key={d.code} value={d.code}>{d.name ? `${d.code} - ${d.name}` : d.code}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170 }} disabled={!departments.length}>
              <InputLabel id="mcsr-bulk-department">Department</InputLabel>
              <Select labelId="mcsr-bulk-department" label="Department" value={department} onChange={(e) => setDepartment(String(e.target.value))}>
                <MenuItem value="">All departments</MenuItem>
                {departments.map((d) => <MenuItem key={d.code} value={d.code}>{d.name ? `${d.code} - ${d.name}` : d.code}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <FormControlLabel
            control={<Checkbox checked={parentsOnly} onChange={(e) => setParents(e.target.checked)} />}
            label="Parent projects only (leave out the child projects; otherwise a parent comes with its children)"
          />

          {catalog && !catalog.sapProjects ? (
            <Alert severity="warning">SAP's project list could not be read: parent projects, divisions and departments are not shown.</Alert>
          ) : null}
          {loadError ? <Alert severity="error">{loadError}</Alert> : null}

          <FormControlLabel
            sx={{ mb: -1 }}
            control={(
              <Checkbox
                checked={allShownOn}
                indeterminate={!allShownOn && someShownOn}
                disabled={!pickable.length}
                onChange={() => setSelected((prev) => toggleAllShown(prev, shown))}
              />
            )}
            label={`Select every project shown that has an MCSR (${pickable.length})`}
          />
          <Box sx={{ maxHeight: { xs: 'none', md: 360 }, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress aria-label="Loading projects" /></Box>
            ) : (
              <ResponsiveTable
                ariaLabel="MCSR projects"
                columns={columns}
                rows={rows}
                getRowKey={(r) => r.p.project}
                emptyMessage={projects.length ? 'No project matches the filters' : 'No MCSR projects'}
                size="small"
              />
            )}
          </Box>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2">
              <strong>{selected.size}</strong> project{selected.size === 1 ? '' : 's'} selected
              {childCount ? ` (${childCount} child project${childCount === 1 ? '' : 's'})` : ''} · {label}
            </Typography>
            {selected.size ? <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button> : null}
          </Stack>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Produce</Typography>
            <FormGroup row>
              <FormControlLabel
                control={<Checkbox checked={outputs.pdf} onChange={(e) => setOutputs((o) => ({ ...o, pdf: e.target.checked }))} />}
                label={selected.size > 1 ? 'One PDF for printing (each project on its own pages)' : 'PDF'}
              />
              <FormControlLabel
                control={<Checkbox checked={outputs.zip && zipUseful} disabled={!zipUseful} onChange={(e) => setOutputs((o) => ({ ...o, zip: e.target.checked }))} />}
                label="ZIP with one PDF per project"
              />
              <FormControlLabel
                control={<Checkbox checked={outputs.xlsx} onChange={(e) => setOutputs((o) => ({ ...o, xlsx: e.target.checked }))} />}
                label={selected.size > 1 ? 'Excel for analysis (a summary sheet, then one sheet per project)' : 'Excel'}
              />
            </FormGroup>
          </Box>

          {notice ? <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.text}</Alert> : null}

          <Box>
            <Typography variant="subtitle2" gutterBottom>Your runs (kept a week)</Typography>
            {!runs.length ? <Typography variant="body2" color="text.secondary">None yet</Typography> : null}
            <Stack spacing={1}>
              {runs.map((run) => {
                const active = run.status === 'Queued' || run.status === 'Running';
                return (
                  <Box key={run.runId} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {run.monthLabel} · {run.total} project{run.total === 1 ? '' : 's'}{run.parentsOnly ? ' · parent projects only' : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {new Date(run.createdDate).toLocaleString()}{run.createdByName ? ` · ${run.createdByName}` : ''}
                          {run.message ? ` · ${run.message}` : ''}
                        </Typography>
                        {run.errors.length ? (
                          <Typography variant="caption" sx={{ color: '#7a4b00' }} component="div">
                            Left out: {run.errors.map((e) => `${e.project} (${e.message})`).join(', ')}
                          </Typography>
                        ) : null}
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={run.status} color={STATUS_COLOR[run.status]} />
                        {run.hasPdf ? (
                          <Button size="small" variant="outlined" disabled={downloading === `${run.runId}-pdf`} onClick={() => void download(run, 'pdf')}>PDF</Button>
                        ) : null}
                        {run.hasZip ? (
                          <Button size="small" variant="outlined" disabled={downloading === `${run.runId}-zip`} onClick={() => void download(run, 'zip')}>ZIP</Button>
                        ) : null}
                        {run.hasXlsx ? (
                          <Button size="small" variant="outlined" disabled={downloading === `${run.runId}-xlsx`} onClick={() => void download(run, 'xlsx')}>Excel</Button>
                        ) : null}
                        {active ? <Button size="small" color="warning" onClick={() => void cancel(run)}>Cancel</Button> : null}
                      </Stack>
                    </Stack>
                    {active ? (
                      <LinearProgress
                        sx={{ mt: 1 }}
                        variant={run.total ? 'determinate' : 'indeterminate'}
                        value={run.total ? Math.min(100, (run.done / run.total) * 100) : undefined}
                        aria-label={`Run ${run.runId} progress`}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={!canStart} onClick={() => void start()}>
          {starting ? 'Starting…' : `Generate${selected.size ? ` (${selected.size})` : ''}`}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default McsrBulkDialog;
